import React, { useState, useEffect } from 'react';

// SVG Icons
const AddIcon = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
);

const CloseIcon = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
);

const TransactionForm = ({ onSuccess, onCancel }) => {
    const [vehicules, setVehicules] = useState([]);
    const [selectedVehicule, setSelectedVehicule] = useState(null);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [formData, setFormData] = useState({
        category_id: '',
        amount: '',
        description: '',
        type: 'décaissement',
        date: new Date().toISOString().split('T')[0],
        voiture_id: ''
    });

    // Fetch vehicles and categories
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);

                // Fetch vehicles
                const vehiculesRes = await fetch('https://krili-loto.com/api/voitures');
                if (!vehiculesRes.ok) throw new Error('Erreur lors du chargement des véhicules');
                const vehiculesData = await vehiculesRes.json();
                setVehicules(vehiculesData);

                // Fetch categories
                const categoriesRes = await fetch('https://krili-loto.com/api/categories');
                if (!categoriesRes.ok) throw new Error('Erreur lors du chargement des catégories');
                const categoriesData = await categoriesRes.json();
                setCategories(categoriesData);

            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.voiture_id) {
            setError('Veuillez sélectionner un véhicule');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await fetch('https://krili-loto.com/api/transactionscar', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData)
            });

            const data = await response.json();

            if (data.success) {
                onSuccess(data.data);
            } else {
                setError(data.message || 'Erreur lors de la création');
            }
        } catch (err) {
            setError('Erreur de connexion au serveur');
        } finally {
            setLoading(false);
        }
    };

    const handleVehiculeChange = (e) => {
        const vehiculeId = e.target.value;
        const vehicule = vehicules.find(v => v.id === parseInt(vehiculeId));

        setSelectedVehicule(vehicule);
        setFormData(prev => ({ ...prev, voiture_id: vehiculeId }));
    };

    if (loading && vehicules.length === 0) {
        return (
            <div className="flex justify-center items-center p-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
            <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-6 shrink-0">
                <div className="flex items-center justify-between">
                    <h2 className="text-2xl font-bold text-white">Nouvelle Transaction</h2>
                    <button
                        onClick={onCancel}
                        className="text-white/80 hover:text-white text-2xl"
                    >
                        ×
                    </button>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6 overflow-y-auto flex-1">
                {error && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                        <div className="flex items-center">
                            <svg className="w-5 h-5 text-red-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                            </svg>
                            <span className="text-red-700 font-medium">{error}</span>
                        </div>
                    </div>
                )}

                {/* Vehicle Selection */}
                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Véhicule *
                    </label>
                    <select
                        value={formData.voiture_id}
                        onChange={handleVehiculeChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                        required
                    >
                        <option value="">Sélectionnez un véhicule</option>
                        {vehicules.map(vehicule => (
                            <option key={vehicule.id} value={vehicule.id}>
                                {vehicule.marque} {vehicule.modele} - {vehicule.matricule}
                            </option>
                        ))}
                    </select>

                    {selectedVehicule && (
                        <div className="mt-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                            <div className="flex items-center space-x-3">
                                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                <span className="text-sm text-blue-700">
                                    Véhicule sélectionné: <strong>{selectedVehicule.marque} {selectedVehicule.modele}</strong>
                                    ({selectedVehicule.matricule})
                                </span>
                            </div>
                        </div>
                    )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Type */}
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Type de transaction *
                        </label>
                        <select
                            value={formData.type}
                            onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                            required
                        >
                            <option value="décaissement">💸 Décaissement</option>
                            <option value="encaissement">💰 Encaissement</option>
                        </select>
                    </div>

                    {/* Amount */}
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Montant (DA) *
                        </label>
                        <input
                            type="number"
                            step="0.01"
                            value={formData.amount}
                            onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                            placeholder="0.00"
                            required
                        />
                    </div>

                    {/* Category */}
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Catégorie
                        </label>
                        <select
                            value={formData.category_id}
                            onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                        >
                            <option value="">Sélectionnez une catégorie</option>
                            {categories.map((cat) => (
                                <option key={cat.id} value={cat.id}>
                                    {cat.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Date */}
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Date *
                        </label>
                        <input
                            type="date"
                            value={formData.date}
                            onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                            required
                        />
                    </div>
                </div>

                {/* Description */}
                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Description
                    </label>
                    <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        rows="3"
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                        placeholder="Description de la transaction..."
                    />
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-4 pt-6 border-t border-gray-200">
                    <button
                        type="submit"
                        disabled={loading || !formData.voiture_id}
                        className="flex items-center space-x-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-8 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {loading ? (
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        ) : (
                            <AddIcon />
                        )}
                        <span className="font-semibold">Créer la transaction</span>
                    </button>

                    <button
                        type="button"
                        onClick={onCancel}
                        className="flex items-center space-x-3 bg-gradient-to-r from-gray-400 to-gray-500 text-white px-8 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 active:scale-95"
                    >
                        <CloseIcon />
                        <span className="font-semibold">Annuler</span>
                    </button>
                </div>
            </form>
        </div>
    );
};

export default TransactionForm;